import { BIBLE_BOOKS, OT_BOOKS, NT_BOOKS } from "../../../mocks/bibleBooks";
import { EN_TRANSLATIONS } from "../../../mocks/bibleTranslations";

interface BibleControlsProps {
  translation: string;
  bookId: number;
  chapter: number;
  accentColor: string;
  isDark: boolean;
  onTranslationChange: (code: string) => void;
  onBookChange: (id: number) => void;
  onChapterChange: (ch: number) => void;
}

export default function BibleControls({
  translation,
  bookId,
  chapter,
  accentColor,
  isDark,
  onTranslationChange,
  onBookChange,
  onChapterChange,
}: BibleControlsProps) {
  const book = BIBLE_BOOKS.find((b) => b.id === bookId)!;
  const chapterCount = book?.chapters ?? 1;

  const containerBg = isDark ? "bg-[#111] border-[#1e1e1e]" : "bg-white border-[#e0d8ce]";
  const labelColor = isDark ? "text-[#555]" : "text-[#aaa]";
  const inactiveBtn = isDark
    ? "bg-[#1a1a1a] text-[#555] hover:text-white border border-[#2e2e2e]"
    : "bg-[#f0ebe3] text-[#888] hover:text-[#1a1a1a] border border-[#ddd5c8]";
  const selectBg = isDark
    ? "bg-[#1a1a1a] border-[#2e2e2e] text-white"
    : "bg-[#f7f3ee] border-[#ddd5c8] text-[#1a1a1a]";

  return (
    <div className={`border rounded-xl p-4 flex flex-col gap-4 ${containerBg}`}>
      {/* Translation */}
      <div>
        <label className={`text-xs uppercase tracking-widest block mb-2 ${labelColor}`}>Translation</label>
        <select
          value={translation}
          onChange={(e) => onTranslationChange(e.target.value)}
          className={`w-full border text-sm rounded-md px-3 py-2 cursor-pointer focus:outline-none ${selectBg}`}
        >
          {EN_TRANSLATIONS.map((t) => (
            <option key={t.code} value={t.code}>
              {t.code} — {t.name}
            </option>
          ))}
        </select>
      </div>

      {/* OT */}
      <div>
        <label className={`text-xs uppercase tracking-widest block mb-2 ${labelColor}`}>Old Testament</label>
        <div className="flex flex-wrap gap-1.5">
          {OT_BOOKS.map((b) => (
            <button
              key={b.id}
              onClick={() => { onBookChange(b.id); onChapterChange(1); }}
              className={`px-2 py-1 rounded text-xs transition-colors cursor-pointer whitespace-nowrap ${
                bookId === b.id ? "text-white" : inactiveBtn
              }`}
              style={bookId === b.id ? { backgroundColor: accentColor } : {}}
            >
              {b.abbr}
            </button>
          ))}
        </div>
      </div>

      {/* NT */}
      <div>
        <label className={`text-xs uppercase tracking-widest block mb-2 ${labelColor}`}>New Testament</label>
        <div className="flex flex-wrap gap-1.5">
          {NT_BOOKS.map((b) => (
            <button
              key={b.id}
              onClick={() => { onBookChange(b.id); onChapterChange(1); }}
              className={`px-2 py-1 rounded text-xs transition-colors cursor-pointer whitespace-nowrap ${
                bookId === b.id ? "text-white" : inactiveBtn
              }`}
              style={bookId === b.id ? { backgroundColor: accentColor } : {}}
            >
              {b.abbr}
            </button>
          ))}
        </div>
      </div>

      {/* Chapter */}
      <div>
        <label className={`text-xs uppercase tracking-widest block mb-2 ${labelColor}`}>
          Chapter — {book?.name}
        </label>
        <div className="flex flex-wrap gap-1.5">
          {Array.from({ length: chapterCount }, (_, i) => i + 1).map((ch) => (
            <button
              key={ch}
              onClick={() => onChapterChange(ch)}
              className={`w-8 h-8 rounded text-xs transition-colors cursor-pointer ${
                chapter === ch ? "text-white" : inactiveBtn
              }`}
              style={chapter === ch ? { backgroundColor: accentColor } : {}}
            >
              {ch}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
